import { useCallback, useEffect, useMemo } from 'react';

import { useJobNotesJunctionList } from '../api/generated_hooks/generated-job_notes_junction-hooks.js';
import { useLotsNotesJunctionList } from '../api/generated_hooks/generated-lots_notes_junction-hooks.js';
import { useNotificationsList, useUpdateNotifications } from '../api/generated_hooks/generated-notifications-hooks.js';
import { useUserMentionsList, useUpdateUserMentions } from '../api/generated_hooks/generated-user_mentions-hooks.js';
import type { Notifications } from '../api/generated_schemas/generated-notifications-schema.js';
import type { UserMentions } from '../api/generated_schemas/generated-user_mentions-schema.js';

/** How often the badge re-reads both notification streams. */
export const NOTIFICATIONS_POLL_INTERVAL_MS = 30_000;

/**
 * A single item in the unified, sorted notification stream.
 *
 * The stream merges two independent sources that the app has always kept
 * separate:
 *  - `notification` — a row from the `notifications` table (activity/phase/
 *    scheduler events), keyed by recipient `user_id = user_profiles.id`.
 *  - `mention` — a row from the `user_mentions` table (@mentions in notes),
 *    keyed by `user_profile_id = user_profiles.id`.
 *
 * The recipient-id semantics of each stream are preserved EXACTLY as the two
 * queries have always used them — do not "unify" or "fix" them here.
 */
export type CombinedNotification =
  | { type: 'notification'; data: Notifications; timestamp: Date }
  | { type: 'mention'; data: UserMentions; timestamp: Date };

/**
 * Resolved deep-link context for a mention (which lot/job the note belongs to).
 * Returned by {@link UseNotificationsResult.getMentionContext}.
 */
export interface MentionContext {
  type: 'lot' | 'job';
  id: string;
  label: string;
  subtitle: string;
  url: string;
}

/**
 * The public API surface of {@link useNotifications}.
 *
 * Shaped so both the existing bell dropdown (`NotificationBadge`) and a future
 * Catch-Up card stack can drive the exact same source of truth. The hook owns
 * ALL data concerns (dual-stream fetch, merge/sort, counts, read toggles,
 * deep-link resolution, 30-second polling). Consumers own only their own UI
 * state (open/closed, active tab, presentation formatting).
 */
export interface UseNotificationsResult {
  /** Unread `notifications`-table rows (activity/phase/scheduler events), newest first. */
  unreadActivityNotifications: Notifications[];
  /** Unread `user_mentions`-table rows (@mentions), newest first. */
  unreadMentions: UserMentions[];
  /** Both streams merged and sorted by timestamp, newest first. */
  items: CombinedNotification[];

  /** Count of unread mentions. */
  mentionsCount: number;
  /** Count of unread activity notifications. */
  activityCount: number;
  /** Total unread count (mentions + activity notifications). */
  unreadCount: number;

  /** Refetch both streams (badge count + lists). */
  refetch: () => void;

  /** Whether a read/unread mutation is currently in flight (either stream). */
  isMutating: boolean;

  /** Resolve the lot/job context for a mention (used for deep-linking + labels). */
  getMentionContext: (mention: UserMentions) => MentionContext | null;
  /** Resolve the deep-link target route for a notification (action_url → typed FKs). */
  resolveNotificationUrl: (notification: Notifications) => string | null;

  /** Mark a mention read (is_read=true, read_at=now) and refetch. */
  markMentionRead: (mention: UserMentions) => Promise<void>;
  /** Mark a mention unread (is_read=false, read_at=null) and refetch. */
  markMentionUnread: (mention: UserMentions) => Promise<void>;
  /** Mark a notification read (is_read=true, read_at=now) and refetch. */
  markNotificationRead: (notification: Notifications) => Promise<void>;
  /** Mark a notification unread (is_read=false, read_at=null) and refetch. */
  markNotificationUnread: (notification: Notifications) => Promise<void>;

  /**
   * Mark all provided items read (is_read=true, read_at=now) in parallel, then
   * refetch. Returns the number of items marked. Callers pass the exact set they
   * want marked (e.g. filtered by the active tab).
   */
  markAllRead: (opts?: {
    mentions?: UserMentions[];
    notifications?: Notifications[];
  }) => Promise<number>;
}

/**
 * useNotifications — the single source of truth for the in-app notification
 * streams.
 *
 * Extracted verbatim from `NotificationBadge.tsx` so the bell dropdown and the
 * future Catch-Up card stack consume ONE implementation. Behavior is identical
 * to the badge's prior inline logic: same two queries (same filters, sort,
 * query keys), same merge/sort, same counts, same read toggles, same 30s
 * polling, same deep-link resolution.
 *
 * @param currentUserId `user_profiles.id` of the signed-in user (AuthUser.id).
 *   Used as `user_id` for the notifications stream AND `user_profile_id` for
 *   the mentions stream — the pre-existing (and intentional) recipient-id
 *   semantics of each stream.
 */
export function useNotifications(currentUserId: string): UseNotificationsResult {
  // Fetch unread mentions for current user (keyed by user_profile_id)
  const { data: unreadMentions = [], refetch: refetchMentions } = useUserMentionsList({
    filters: {
      user_profile_id: currentUserId,
      is_read: false
    },
    sortBy: 'mentioned_at',
    sortOrder: 'desc'
  });

  // Fetch unread activity notifications for current user (keyed by user_id)
  const { data: unreadActivityNotifications = [], refetch: refetchNotifications } = useNotificationsList({
    filters: {
      user_id: currentUserId,
      is_read: false
    },
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  // Combined refetch function
  const refetch = useCallback(() => {
    refetchMentions();
    refetchNotifications();
  }, [refetchMentions, refetchNotifications]);

  const mentionsCount = unreadMentions.length;
  const activityCount = unreadActivityNotifications.length;
  const unreadCount = mentionsCount + activityCount;

  // Mutation for updating mentions
  const updateMentionMutation = useUpdateUserMentions();

  // Mutation for updating activity notifications
  const updateNotificationMutation = useUpdateNotifications();

  const isMutating = updateMentionMutation.isPending || updateNotificationMutation.isPending;

  // Fetch junction data to determine context (lot vs job)
  // Get all note_ids from unread mentions
  const noteIds = useMemo(() => unreadMentions.map(m => m.note_id), [unreadMentions]);
  const noteIdSet = useMemo(() => new Set(noteIds), [noteIds]);

  // Query lot and job junctions (org-scoped by server), then filter client-side
  // Note: Generated hooks don't support array filters, so we fetch all and filter
  const { data: rawLotJunctions = [] } = useLotsNotesJunctionList({
    limit: 1000,
  });

  const { data: rawJobJunctions = [] } = useJobNotesJunctionList({
    limit: 1000,
  });

  // Filter to only junctions matching our mention note_ids
  const lotJunctions = useMemo(
    () => rawLotJunctions.filter(j => noteIdSet.has(j.note_id)),
    [rawLotJunctions, noteIdSet]
  );
  const jobJunctions = useMemo(
    () => rawJobJunctions.filter(j => noteIdSet.has(j.note_id)),
    [rawJobJunctions, noteIdSet]
  );

  // Create lookup maps for quick access
  const lotJunctionMap = useMemo(
    () => new Map(lotJunctions.map(j => [j.note_id, j])),
    [lotJunctions]
  );
  const jobJunctionMap = useMemo(
    () => new Map(jobJunctions.map(j => [j.note_id, j])),
    [jobJunctions]
  );

  // Combined and sorted list - merge both types and sort by timestamp
  const items = useMemo<CombinedNotification[]>(() => {
    const notifications: CombinedNotification[] = unreadActivityNotifications.map(n => ({
      type: 'notification' as const,
      data: n,
      timestamp: new Date(n.created_at || 0)
    }));

    const mentions: CombinedNotification[] = unreadMentions.map(m => ({
      type: 'mention' as const,
      data: m,
      timestamp: new Date(m.mentioned_at || 0)
    }));

    // Combine and sort by timestamp descending (most recent first)
    return [...notifications, ...mentions].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }, [unreadActivityNotifications, unreadMentions]);

  // Poll both streams every 30 seconds. This was the fallback behind a
  // Supabase Realtime subscription; Realtime never connected in production,
  // so the poll was what users got. The browser no longer holds a Supabase
  // client (decision 1 = A: the API is the only way to the database), so the
  // poll is now the only path. Same interval as before.
  useEffect(() => {
    const pollingInterval = setInterval(() => {
      refetch();
    }, NOTIFICATIONS_POLL_INTERVAL_MS);

    return () => {
      clearInterval(pollingInterval);
    };
  }, [refetch]);

  // Get context information for a mention (deep-link target + display labels)
  const getMentionContext = useCallback((mention: UserMentions): MentionContext | null => {
    // Check if this note is associated with a lot
    const lotJunction = lotJunctionMap.get(mention.note_id);
    if (lotJunction) {
      return {
        type: 'lot' as const,
        id: lotJunction.lot_id,
        label: `Lot ${lotJunction.trig_lot_number || ''}`,
        subtitle: lotJunction.trig_lot_address || '',
        url: `/lots/${lotJunction.lot_id}`
      };
    }

    // Check if this note is associated with a job
    const jobJunction = jobJunctionMap.get(mention.note_id);
    if (jobJunction) {
      return {
        type: 'job' as const,
        id: jobJunction.job_id,
        label: `Job ${jobJunction.trig_job_number || ''}`,
        subtitle: jobJunction.trig_job_name || '',
        url: `/job-view/${jobJunction.job_id}`
      };
    }

    return null;
  }, [lotJunctionMap, jobJunctionMap]);

  // Resolve the deep-link target for a notification: action_url, then typed FKs.
  // Mirrors the badge's original navigation cascade exactly.
  const resolveNotificationUrl = useCallback((notification: Notifications): string | null => {
    if (notification.action_url) {
      return notification.action_url;
    }
    if (notification.activity_id) {
      return `/activities/${notification.activity_id}`;
    }
    if (notification.lot_id) {
      return `/lots/${notification.lot_id}`;
    }
    if (notification.job_id) {
      return `/job-view/${notification.job_id}`;
    }
    return null;
  }, []);

  // Mark a mention read and refetch
  const markMentionRead = useCallback(async (mention: UserMentions) => {
    await updateMentionMutation.mutateAsync({
      id: mention.id!,
      data: {
        is_read: true,
        read_at: new Date().toISOString()
      } as any // API accepts partial updates
    });
    await refetch();
  }, [updateMentionMutation, refetch]);

  // Mark a mention unread and refetch
  const markMentionUnread = useCallback(async (mention: UserMentions) => {
    await updateMentionMutation.mutateAsync({
      id: mention.id!,
      data: {
        is_read: false,
        read_at: null
      } as any // API accepts partial updates
    });
    await refetch();
  }, [updateMentionMutation, refetch]);

  // Mark a notification read and refetch
  const markNotificationRead = useCallback(async (notification: Notifications) => {
    await updateNotificationMutation.mutateAsync({
      id: notification.id!,
      data: {
        is_read: true,
        read_at: new Date().toISOString()
      } as any // API accepts partial updates
    });
    await refetch();
  }, [updateNotificationMutation, refetch]);

  // Mark a notification unread and refetch
  const markNotificationUnread = useCallback(async (notification: Notifications) => {
    await updateNotificationMutation.mutateAsync({
      id: notification.id!,
      data: {
        is_read: false,
        read_at: null
      } as any // API accepts partial updates
    });
    await refetch();
  }, [updateNotificationMutation, refetch]);

  // Mark all provided items read in parallel, then refetch. Returns count marked.
  const markAllRead = useCallback(async (opts?: {
    mentions?: UserMentions[];
    notifications?: Notifications[];
  }): Promise<number> => {
    const mentionsToMark = opts?.mentions ?? [];
    const notificationsToMark = opts?.notifications ?? [];
    const now = new Date().toISOString();

    const mentionPromises = mentionsToMark.map(mention =>
      updateMentionMutation.mutateAsync({
        id: mention.id!,
        data: {
          is_read: true,
          read_at: now
        } as any // API accepts partial updates
      })
    );

    const notificationPromises = notificationsToMark.map(notification =>
      updateNotificationMutation.mutateAsync({
        id: notification.id!,
        data: {
          is_read: true,
          read_at: now
        } as any // API accepts partial updates
      })
    );

    await Promise.all([...mentionPromises, ...notificationPromises]);
    await refetch();

    return mentionsToMark.length + notificationsToMark.length;
  }, [updateMentionMutation, updateNotificationMutation, refetch]);

  return {
    unreadActivityNotifications,
    unreadMentions,
    items,
    mentionsCount,
    activityCount,
    unreadCount,
    refetch,
    isMutating,
    getMentionContext,
    resolveNotificationUrl,
    markMentionRead,
    markMentionUnread,
    markNotificationRead,
    markNotificationUnread,
    markAllRead,
  };
}
